import { useRef, useEffect, useState, useMemo } from 'react';
import { Button } from 'primereact/button';
import { Dropdown } from 'primereact/dropdown';
import { Message } from 'primereact/message';
import 'primereact/resources/themes/lara-light-blue/theme.css';
import 'primereact/resources/primereact.min.css';
import { InputText } from 'primereact/inputtext';
import { useNavigate, useParams } from 'react-router-dom';
import { InputTextarea } from 'primereact/inputtextarea';
import api from '../../services/api';
import { Card } from 'primereact/card';
import { Toast } from 'primereact/toast';
import { LANG_OPTIONS, tagOptions } from '../../constants/constants';
import { Controller, useForm } from 'react-hook-form';
import MetaTagsLiveCodePreview from '../EntryPages/EntryPage/MetaTagsLiveCodePreview';
import { Panel } from 'primereact/panel';
import { ProgressBar } from 'primereact/progressbar';
import { Checkbox } from 'primereact/checkbox';
import { Dialog } from 'primereact/dialog';
import { MultiSelect } from 'primereact/multiselect';
import TranslationsManager from '../../components/TranslationsManager';
import ProseMirrorComponent from '../../components/AdvancedProseMirrorEditor';
import {
  COUNTRY_OPTIONS,
  COUNTRY_SECTION_CATALOG,
  HEADING_LEVEL_OPTIONS,
  clampHeadingLevel,
  createSection,
  normalizeSectionsForEditor,
  serializeSectionsForSave,
} from './countrySectionCatalog';
import CarouselSectionEditor from './CarouselSectionEditor';
import {
  COUNTRY_SEO_PACK_OPTIONS,
  buildCountrySeoPackTags,
  mergeCountrySeoPackTags,
} from './countrySeoPack';
import PreviewCountry from './PreviewCountry';

function CountryPagesManager() {
  const { id } = useParams();
  const toast = useRef(null);
  const navigate = useNavigate();

  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingPage, setIsLoadingPage] = useState(Boolean(id));
  const [metaTags, setMetaTags] = useState([{ attributes: [{ key: '', value: '' }] }]);
  const [selectedSectionType, setSelectedSectionType] = useState(null);
  const [revalidationLoading, setRevalidationLoading] = useState(false);
  const [isRevalidateLoading, setIsRevalidateLoading] = useState(false);
  const [previewVisible, setPreviewVisible] = useState(false);
  const [seoPackDialogVisible, setSeoPackDialogVisible] = useState(false);
  const [seoPackSelected, setSeoPackSelected] = useState([]);
  const [seoPackGenerating, setSeoPackGenerating] = useState(false);
  const [seoPanelCollapsed, setSeoPanelCollapsed] = useState(true);
  const [contentPanelCollapsed, setContentPanelCollapsed] = useState(true);
  const [faqsPanelCollapsed, setFaqsPanelCollapsed] = useState(true);
  const [translationsPanelCollapsed, setTranslationsPanelCollapsed] = useState(true);

  const public_url = process.env.REACT_APP_BORDERGARITA_PUBLIC_APP_URL || '';

  const { control, handleSubmit, setValue, trigger, getValues, reset, watch } = useForm({
    defaultValues: {
      _id: '',
      lang: '',
      country: '',
      slug: '',
      full_slug: '',
      title: '',
      hero: {
        breadcrumb: '',
        eyebrow: '',
        title: '',
        description: '',
        image: {
          url: '',
          alt: '',
          title: '',
          caption: '',
          width: '',
          height: '',
          credits: {
            source: { text: '', url: '' },
            author: { text: '', url: '' },
          },
        },
      },
      image: {
        url: '',
        alt: '',
        title: '',
        caption: '',
        width: '',
        height: '',
        credits: {
          source: { text: '', url: '' },
          author: { text: '', url: '' },
        },
      },
      sections: [],
      faqs_section: { h3: '', p: '', items: [] },
      featuredPlaceIds: [],
      translations: [],
      revalidated: false,
      revalidated_at: '',
      auto_revalidate: false,
    },
  });

  const watchLang = watch('lang');
  const watchCountry = watch('country');
  const watchSlug = watch('slug');
  const watchFullSlug = watch('full_slug');
  const watchedSectionsRaw = watch('sections');
  const watchSections = useMemo(() => watchedSectionsRaw || [], [watchedSectionsRaw]);
  const watchTranslations = watch('translations');
  const watchFaqsItems = watch('faqs_section.items') || [];
  const watchHero = watch('hero');
  const watchRevalidated = watch('revalidated');
  const watchId = watch('_id');
  const [placeOptions, setPlaceOptions] = useState([]);

  const showContentPanels = Boolean(watchId && watchRevalidated);

  useEffect(() => {
    api
      .get('/crossing_name_places')
      .then((res) => {
        setPlaceOptions(
          (res.data || []).map((p) => ({
            label: `${p.name}${p.crossingName ? ` · ${p.crossingName}` : ''}`,
            value: p._id,
          }))
        );
      })
      .catch(() => {});
  }, []);

  const convertToMetaTagsFormat = (tags) =>
    tags?.map((tag) => {
      const attributesArray = Object.entries(tag.attributes || {}).map(([key, value]) => ({
        key: key.trim(),
        value: value.toString().trim(),
      }));
      const typeTag = tagOptions.find((option) => option.code === tag.type) || {};
      return {
        type: typeTag || {},
        content: tag.content || '',
        name: tag.name || '',
        attributes: attributesArray,
      };
    });

  useEffect(() => {
    if (!id) {
      setIsLoadingPage(false);
      return;
    }
    setIsLoadingPage(true);
    api
      .get(`/country_pages/${id}`)
      .then((response) => {
        const data = response.data;
        const oldTags = convertToMetaTagsFormat(data?.tags || []);
        setMetaTags(oldTags?.length ? oldTags : [{ attributes: [{ key: '', value: '' }] }]);
        const selectedLang = LANG_OPTIONS.find((l) => l.code === data.lang) || '';
        const selectedCountry = data.country || '';
        const sections = normalizeSectionsForEditor(
          Array.isArray(data.sections) ? data.sections : []
        );
        const emptyImage = {
          url: '',
          alt: '',
          title: '',
          caption: '',
          width: '',
          height: '',
          credits: {
            source: { text: '', url: '' },
            author: { text: '', url: '' },
          },
        };
        const heroImage = {
          ...emptyImage,
          ...(data.hero?.image || data.image || {}),
          credits: {
            source: {
              text: data.hero?.image?.credits?.source?.text || '',
              url: data.hero?.image?.credits?.source?.url || '',
            },
            author: {
              text: data.hero?.image?.credits?.author?.text || '',
              url: data.hero?.image?.credits?.author?.url || '',
            },
          },
          width: data.hero?.image?.width ?? data.image?.width ?? '',
          height: data.hero?.image?.height ?? data.image?.height ?? '',
        };

        reset({
          _id: data._id,
          slug: data.slug ?? '',
          full_slug: data.full_slug ?? '',
          lang: selectedLang,
          country: selectedCountry,
          title: data.title || data.hero?.title || '',
          hero: {
            breadcrumb: data.hero?.breadcrumb || '',
            eyebrow: data.hero?.eyebrow || '',
            title: data.hero?.title || data.title || '',
            description: data.hero?.description || '',
            image: heroImage,
          },
          image: heroImage,
          sections,
          faqs_section: {
            h3: data.faqs_section?.h3 || '',
            p: data.faqs_section?.p || '',
            items: (data.faqs_section?.items || []).map((faq) => ({
              question: faq.question || '',
              answer: faq.answer || '',
              answer_html: faq.answer_html || '',
              answer_html_updated: faq.answer_html || '',
            })),
          },
          featuredPlaceIds: (data.featuredPlaceIds || []).map((p) => p._id || p),
          translations: data.translations || [],
          revalidated: data.revalidated ?? false,
          revalidated_at: data.revalidated_at ?? '',
          auto_revalidate: data.auto_revalidate ?? false,
        });
      })
      .catch((error) => {
        console.error(error);
        toast.current?.show({
          severity: 'error',
          summary: 'Error',
          detail: 'Could not load country page',
          life: 3000,
        });
      })
      .finally(() => setIsLoadingPage(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => {
    const langSegment = (watchLang?.code || '').toLowerCase();
    const countryCode =
      typeof watchCountry === 'string'
        ? watchCountry
        : watchCountry?.code || watchCountry?.slug || '';
    if (countryCode && countryCode !== watchSlug) {
      setValue('slug', countryCode);
    }
    if (langSegment && countryCode) {
      setValue('full_slug', `/${langSegment}/${countryCode}`.replace(/\/{2,}/g, '/'));
    }
  }, [watchLang, watchCountry, watchSlug, setValue]);

  const getFinalMetaTags = useMemo(
    () =>
      metaTags?.map((tag) => {
        const finalTag = {
          type: tag?.type?.code,
          name: tag.name || '',
          self_closing: tag?.type?.self_closing || false,
          content: tag?.content || '',
          attributes: {},
        };
        if (tag.attributes && Array.isArray(tag.attributes)) {
          tag.attributes.forEach((attr) => {
            if (attr.key && attr.key.trim() && attr.value !== undefined) {
              finalTag.attributes[attr.key.trim()] = attr.value.toString().trim();
            }
          });
        }
        return finalTag;
      }),
    [metaTags]
  );

  const addMetaTag = () => {
    const nextIndex = metaTags?.length || 0;
    const wasCollapsed = seoPanelCollapsed;
    setMetaTags([...(metaTags || []), { attributes: [{ key: '', value: '' }] }]);
    if (wasCollapsed) setSeoPanelCollapsed(false);
    setTimeout(() => {
      document
        .getElementById(`seo-element-${nextIndex}`)
        ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, wasCollapsed ? 180 : 80);
  };
  const removeMetaTag = (tagIndex) => setMetaTags(metaTags.filter((_, i) => i !== tagIndex));
  const addAttribute = (tagIndex) => {
    const updated = [...metaTags];
    updated[tagIndex].attributes.push({ key: '', value: '' });
    setMetaTags(updated);
  };
  const removeAttribute = (tagIndex, attrIndex) => {
    const updated = [...metaTags];
    updated[tagIndex].attributes = updated[tagIndex].attributes.filter((_, i) => i !== attrIndex);
    setMetaTags(updated);
  };
  const updateAttribute = (tagIndex, attrIndex, field, newValue) => {
    const updated = [...metaTags];
    updated[tagIndex].attributes[attrIndex][field] = newValue;
    setMetaTags(updated);
  };
  const updateMainAttribute = (tagIndex, field, newValue) => {
    const updated = [...metaTags];
    updated[tagIndex][field] = newValue;
    setMetaTags(updated);
  };
  const updateTagType = (tagIndex, type) => {
    const updated = [...metaTags];
    updated[tagIndex].type = type;
    setMetaTags(updated);
  };

  const resolveTranslationSlugs = async (translations) => {
    try {
      const { data } = await api.get('/country_pages/translations');
      const byId = {};
      (Array.isArray(data) ? data : []).forEach((page) => {
        if (page?._id) byId[page._id] = page.full_slug || '';
      });
      return (translations || []).map((t) => ({
        ...t,
        full_slug: byId[t.country_page_id] || t.full_slug || '',
      }));
    } catch {
      return translations || [];
    }
  };

  const handleGenerateSeoPack = async () => {
    if (!seoPackSelected.length) {
      toast.current?.show({
        severity: 'warn',
        summary: 'SEO pack',
        detail: 'Select at least one pack',
        life: 3000,
      });
      return;
    }
    setSeoPackGenerating(true);
    try {
      const hero = getValues('hero') || {};
      const translations = await resolveTranslationSlugs(getValues('translations') || []);
      const faqs = getValues('faqs_section.items') || [];
      const siteUrl = (public_url || 'https://bordergarita.com').replace(/\/$/, '');
      const countryVal = getValues('country');
      const countryCode =
        typeof countryVal === 'string' ? countryVal : countryVal?.code || '';

      const incoming = buildCountrySeoPackTags(
        {
          title: hero.title || getValues('title') || '',
          description: hero.description || '',
          image: (hero.image?.url || getValues('image.url') || '').trim(),
          full_slug: getValues('full_slug') || '',
          lang: getValues('lang'),
          country: countryCode,
          translations,
          faqs,
          siteUrl,
        },
        seoPackSelected
      ).map((tag) => {
        const matchedType = tagOptions.find((o) => o.code === tag.type?.code);
        return matchedType ? { ...tag, type: matchedType } : tag;
      });

      const { tags, added, skipped } = mergeCountrySeoPackTags(metaTags, incoming);
      setMetaTags(tags.length ? tags : [{ attributes: [{ key: '', value: '' }] }]);
      setSeoPackDialogVisible(false);
      toast.current?.show({
        severity: added ? 'success' : 'info',
        summary: 'SEO pack',
        detail:
          skipped > 0
            ? `Added ${added} tag(s). Skipped ${skipped} (already present).`
            : `Added ${added} tag(s).`,
        life: 4000,
      });
    } finally {
      setSeoPackGenerating(false);
    }
  };

  const handleAddFaq = () => {
    const faqs = getValues('faqs_section.items') || [];
    const nextIndex = faqs.length;
    setValue('faqs_section.items', [
      ...faqs,
      { question: '', answer: '', answer_html: '', answer_html_updated: '' },
    ]);
    trigger('faqs_section.items');
    setTimeout(() => {
      document.getElementById(`faq-question-${nextIndex}`)?.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      });
    }, 50);
  };
  const handleRemoveFaq = (idx) => {
    const faqs = getValues('faqs_section.items') || [];
    setValue(
      'faqs_section.items',
      faqs.filter((_, i) => i !== idx)
    );
    trigger('faqs_section.items');
  };
  const handleFaqChange = (idx, field, value) => {
    const faqs = [...(getValues('faqs_section.items') || [])];
    faqs[idx] = { ...faqs[idx], [field]: value };
    setValue('faqs_section.items', faqs);
    trigger('faqs_section.items');
  };

  const handleAddTranslation = () => {
    const current = getValues('translations') || [];
    setValue('translations', [...current, { lang: '', country_page_id: '' }], {
      shouldDirty: true,
    });
  };
  const handleRemoveTranslation = (index) => {
    const current = getValues('translations') || [];
    setValue(
      'translations',
      current.filter((_, i) => i !== index),
      { shouldDirty: true }
    );
  };

  const addSection = () => {
    if (!selectedSectionType) {
      toast.current?.show({
        severity: 'warn',
        summary: 'Warning',
        detail: 'Select a section type',
        life: 3000,
      });
      return;
    }
    const current = getValues('sections') || [];
    const created = createSection(selectedSectionType);
    setValue('sections', [...current, created]);
    setSelectedSectionType(null);
    setTimeout(() => {
      document
        .getElementById(`country-section-${created.id}`)
        ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 80);
  };

  const removeSection = (index) => {
    const current = [...(getValues('sections') || [])];
    current.splice(index, 1);
    setValue('sections', current);
  };

  const moveSection = (index, direction) => {
    const current = [...(getValues('sections') || [])];
    const target = index + direction;
    if (target < 0 || target >= current.length) return;
    const tmp = current[index];
    current[index] = current[target];
    current[target] = tmp;
    setValue('sections', current);
  };

  const updateSectionData = (index, key, value) => {
    const current = [...(getValues('sections') || [])];
    current[index] = {
      ...current[index],
      data: { ...current[index].data, [key]: value },
    };
    setValue('sections', current);
  };

  const patchSectionData = (index, patch) => {
    const current = [...(getValues('sections') || [])];
    current[index] = {
      ...current[index],
      data: { ...current[index].data, ...patch },
    };
    setValue('sections', current);
  };

  const updateNestedSectionData = (index, path, value) => {
    const current = [...(getValues('sections') || [])];
    const data = { ...(current[index].data || {}) };
    const parts = path.split('.');
    let cursor = data;
    for (let i = 0; i < parts.length - 1; i += 1) {
      const key = parts[i];
      cursor[key] = { ...(cursor[key] || {}) };
      cursor = cursor[key];
    }
    cursor[parts[parts.length - 1]] = value;
    current[index] = { ...current[index], data };
    setValue('sections', current);
  };

  const openCloudinary = (onResult) => {
    if (!window.cloudinary?.openUploadWidget) {
      toast.current?.show({
        severity: 'warn',
        summary: 'Cloudinary',
        detail: 'Upload widget not available',
        life: 3000,
      });
      return;
    }
    window.cloudinary.openUploadWidget(
      { cloudName: 'dtxar5b36', uploadPreset: 'bordergarita_uploads' },
      (error, result) => {
        if (!error && result.event === 'success') {
          onResult({
            url: result.info.secure_url,
            width: result.info.width,
            height: result.info.height,
          });
          toast.current?.show({
            severity: 'success',
            summary: 'Success',
            detail: 'Image uploaded',
            life: 3000,
          });
        }
      }
    );
  };

  const isValidUrl = useMemo(() => {
    const langSegment = watchLang?.code;
    const countryCode =
      typeof watchCountry === 'string' ? watchCountry : watchCountry?.code;
    return Boolean(langSegment && countryCode);
  }, [watchLang, watchCountry]);

  const onSave = async (data) => {
    const countryCode =
      typeof data.country === 'string' ? data.country : data.country?.code || '';
    const payload = {
      type: 'country_page',
      country: countryCode,
      slug: data.slug || countryCode,
      full_slug: data.full_slug,
      lang: data.lang?.code || data.lang || 'ES',
      title: data.hero?.title || data.title || '',
      hero: data.hero,
      image: data.hero?.image || data.image,
      sections: serializeSectionsForSave(data.sections || []),
      faqs_section: {
        h3: data.faqs_section?.h3 || '',
        p: data.faqs_section?.p || '',
        items: (data.faqs_section?.items || []).map((faq) => ({
          question: faq.question || '',
          answer: faq.answer || '',
          answer_html: faq.answer_html_updated || faq.answer_html || '',
        })),
      },
      featuredPlaceIds: data.featuredPlaceIds || [],
      translations: data.translations || [],
      tags: (getFinalMetaTags || []).filter((tag) => tag?.type),
      revalidated: data.revalidated,
      revalidated_at: data.revalidated_at,
      auto_revalidate: data.auto_revalidate,
    };

    try {
      setIsLoading(true);
      if (id) {
        await api.put(`/country_pages/${id}`, payload);
        toast.current?.show({
          severity: 'success',
          summary: 'Success',
          detail: 'Country page updated',
          life: 3000,
        });
      } else {
        const response = await api.post('/country_pages', payload);
        toast.current?.show({
          severity: 'success',
          summary: 'Success',
          detail: 'Country page created',
          life: 3000,
        });
        navigate(`/country_pages/edit/${response.data._id}`);
      }
    } catch (err) {
      console.error(err);
      toast.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: err?.response?.data?.error || 'Save failed',
        life: 4000,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleFirstRevalidation = async () => {
    if (!id) return;
    setRevalidationLoading(true);
    try {
      const res = await api.put(`/country_pages/${id}`, { revalidated: true });
      setValue('revalidated', true);
      trigger('revalidated');
      toast.current?.show({
        severity: 'success',
        summary: 'Success',
        detail: 'Country page published successfully',
        life: 3000,
      });
      window.open(
        `${public_url}${res.data.full_slug || getValues('full_slug')}`,
        '_blank',
        'width=450,height=700'
      );
    } catch (e) {
      console.error(e);
    } finally {
      setRevalidationLoading(false);
    }
  };

  const onRevalidateUrl = async () => {
    const path = getValues('full_slug');
    const url = process.env.REACT_APP_BORDERGARITA_PUBLIC_APP_URL;
    const secret = process.env.REACT_APP_BORDERGARITA_REVALIDATE_SECRET;
    setIsRevalidateLoading(true);
    try {
      await fetch(`${url}/api/revalidate?secret=${secret}&path=${path}`, { method: 'POST' });
      toast.current?.show({
        severity: 'success',
        summary: 'Success',
        detail: 'URL revalidated',
        life: 3000,
      });
    } catch (err) {
      console.error(err);
    } finally {
      setIsRevalidateLoading(false);
    }
  };

  const renderImageBlockEditor = (data, idx) => (
    <div className="grid">
      <div className="col-12">
        <label>
          <strong>Image URL</strong>
        </label>
        <InputText
          value={data.url || ''}
          onChange={(e) => updateSectionData(idx, 'url', e.target.value)}
          className="w-full"
        />
      </div>
      <div className="col-12">
        <Button
          type="button"
          label="Upload"
          icon="pi pi-cloud-upload"
          onClick={() =>
            openCloudinary(({ url, width, height }) => {
              patchSectionData(idx, {
                url,
                ...(width ? { width } : {}),
                ...(height ? { height } : {}),
              });
            })
          }
        />
      </div>
      <div className="col-12 md:col-6">
        <label>
          <strong>Alt</strong>
        </label>
        <InputText
          value={data.alt || ''}
          onChange={(e) => updateSectionData(idx, 'alt', e.target.value)}
          className="w-full"
        />
      </div>
      <div className="col-12 md:col-6">
        <label>
          <strong>Title</strong>
        </label>
        <InputText
          value={data.title || ''}
          onChange={(e) => updateSectionData(idx, 'title', e.target.value)}
          className="w-full"
        />
      </div>
      <div className="col-6 md:col-3">
        <label>
          <strong>Width</strong>
        </label>
        <InputText
          value={data.width ?? ''}
          onChange={(e) => updateSectionData(idx, 'width', e.target.value)}
          className="w-full"
          placeholder="1200"
        />
      </div>
      <div className="col-6 md:col-3">
        <label>
          <strong>Height</strong>
        </label>
        <InputText
          value={data.height ?? ''}
          onChange={(e) => updateSectionData(idx, 'height', e.target.value)}
          className="w-full"
          placeholder="800"
        />
      </div>
      <div className="col-12 md:col-6">
        <label>
          <strong>Caption</strong>
        </label>
        <InputText
          value={data.caption || ''}
          onChange={(e) => updateSectionData(idx, 'caption', e.target.value)}
          className="w-full"
        />
      </div>
      <div className="col-12">
        <div className="text-xs font-semibold text-color-secondary mb-2">CREDITS</div>
        <div className="grid p-3 border-round surface-ground border-1 surface-border">
          <div className="col-12 md:col-6">
            <label>
              <strong>Source text</strong>
            </label>
            <InputText
              value={data.credits?.source?.text || ''}
              onChange={(e) =>
                updateNestedSectionData(idx, 'credits.source.text', e.target.value)
              }
              className="w-full"
            />
          </div>
          <div className="col-12 md:col-6">
            <label>
              <strong>Source URL</strong>
            </label>
            <InputText
              value={data.credits?.source?.url || ''}
              onChange={(e) =>
                updateNestedSectionData(idx, 'credits.source.url', e.target.value)
              }
              className="w-full"
            />
          </div>
          <div className="col-12 md:col-6">
            <label>
              <strong>Author text</strong>
            </label>
            <InputText
              value={data.credits?.author?.text || ''}
              onChange={(e) =>
                updateNestedSectionData(idx, 'credits.author.text', e.target.value)
              }
              className="w-full"
            />
          </div>
          <div className="col-12 md:col-6">
            <label>
              <strong>Author URL</strong>
            </label>
            <InputText
              value={data.credits?.author?.url || ''}
              onChange={(e) =>
                updateNestedSectionData(idx, 'credits.author.url', e.target.value)
              }
              className="w-full"
            />
          </div>
        </div>
      </div>
      <div className="col-12 flex align-items-center gap-2">
        <Checkbox
          inputId={`pinned-img-${idx}`}
          checked={Boolean(data.pinned)}
          onChange={(e) => updateSectionData(idx, 'pinned', e.checked)}
        />
        <label htmlFor={`pinned-img-${idx}`}>Pinned</label>
      </div>
      {data.url && (
        <div className="col-12">
          <img
            src={data.url}
            alt={data.alt || ''}
            style={{ maxWidth: '100%', maxHeight: 240, borderRadius: 8 }}
          />
        </div>
      )}
    </div>
  );

  const renderSectionEditor = (section, idx) => {
    const data = section.data || {};
    switch (section.type) {
      case 'heading':
        return (
          <div className="grid">
            <div className="col-3">
              <label>
                <strong>Level</strong>
              </label>
              <Dropdown
                value={clampHeadingLevel(data.level)}
                options={HEADING_LEVEL_OPTIONS}
                onChange={(e) => updateSectionData(idx, 'level', e.value)}
                className="w-full"
              />
            </div>
            <div className="col-9">
              <label>
                <strong>Text</strong>
              </label>
              <InputText
                value={data.text || ''}
                onChange={(e) => updateSectionData(idx, 'text', e.target.value)}
                className="w-full"
              />
            </div>
            <div className="col-12">
              <label>
                <strong>Rich text paragraph</strong>
              </label>
              <ProseMirrorComponent
                content={data.html || ''}
                onChange={(htmlContent) => updateSectionData(idx, 'html_updated', htmlContent)}
              />
            </div>
          </div>
        );
      case 'paragraph':
        return (
          <div>
            <label>
              <strong>Paragraph (rich text)</strong>
            </label>
            <ProseMirrorComponent
              content={data.html || ''}
              onChange={(htmlContent) => updateSectionData(idx, 'html_updated', htmlContent)}
            />
          </div>
        );
      case 'rich_text':
        return (
          <div>
            <label>
              <strong>HTML / text</strong>
            </label>
            <InputTextarea
              value={data.html || ''}
              onChange={(e) => updateSectionData(idx, 'html', e.target.value)}
              rows={6}
              className="w-full"
            />
          </div>
        );
      case 'border_cities_table': {
        const rows = Array.isArray(data.rows) ? data.rows : [];
        const updateRow = (rowIndex, field, value) => {
          const next = rows.map((row, i) =>
            i === rowIndex ? { ...row, [field]: value } : row
          );
          updateSectionData(idx, 'rows', next);
        };
        const addRow = () => {
          updateSectionData(idx, 'rows', [...rows, { left: '', right: '' }]);
        };
        const removeRow = (rowIndex) => {
          updateSectionData(
            idx,
            'rows',
            rows.filter((_, i) => i !== rowIndex)
          );
        };
        const moveRow = (rowIndex, dir) => {
          const target = rowIndex + dir;
          if (target < 0 || target >= rows.length) return;
          const next = [...rows];
          const tmp = next[rowIndex];
          next[rowIndex] = next[target];
          next[target] = tmp;
          updateSectionData(idx, 'rows', next);
        };
        return (
          <div className="grid">
            <div className="col-12">
              <label>
                <strong>Optional title above table</strong>
              </label>
              <InputText
                value={data.title || ''}
                onChange={(e) => updateSectionData(idx, 'title', e.target.value)}
                className="w-full"
                placeholder="Optional title above table"
              />
            </div>
            <div className="col-12 md:col-6">
              <label>
                <strong>Left column header</strong>
              </label>
              <InputText
                value={data.left_header || ''}
                onChange={(e) => updateSectionData(idx, 'left_header', e.target.value)}
                className="w-full"
                placeholder="Left column header"
              />
            </div>
            <div className="col-12 md:col-6">
              <label>
                <strong>Right column header</strong>
              </label>
              <InputText
                value={data.right_header || ''}
                onChange={(e) => updateSectionData(idx, 'right_header', e.target.value)}
                className="w-full"
                placeholder="Right column header"
              />
            </div>
            <div className="col-12">
              <div className="flex justify-content-between align-items-center mb-2">
                <strong>Rows ({rows.length})</strong>
                <Button
                  type="button"
                  label="Add row"
                  icon="pi pi-plus"
                  className="p-button-sm mt-1 mb-1"
                   onClick={addRow}
                />
              </div>
              <div className="flex flex-column gap-2">
                {rows.map((row, rowIndex) => (
                  <div
                    key={`border-row-${idx}-${rowIndex}`}
                    className="grid align-items-center p-2 border-1 border-round surface-border"
                  >
                    <div className="col-12 md:col-5">
                      <InputText
                        value={row.left || ''}
                        onChange={(e) => updateRow(rowIndex, 'left', e.target.value)}
                        className="w-full"
                        placeholder="Left cell"
                      />
                    </div>
                    <div className="col-12 md:col-5">
                      <InputText
                        value={row.right || ''}
                        onChange={(e) => updateRow(rowIndex, 'right', e.target.value)}
                        className="w-full"
                        placeholder="Right cell"
                      />
                    </div>
                    <div className="col-12 md:col-2 flex gap-1 justify-content-end">
                      <Button
                        type="button"
                        icon="pi pi-arrow-up"
                        className="p-button-text p-button-sm"
                        disabled={rowIndex === 0}
                        onClick={() => moveRow(rowIndex, -1)}
                      />
                      <Button
                        type="button"
                        icon="pi pi-arrow-down"
                        className="p-button-text p-button-sm"
                        disabled={rowIndex === rows.length - 1}
                        onClick={() => moveRow(rowIndex, 1)}
                      />
                      <Button
                        type="button"
                        icon="pi pi-trash"
                        className="p-button-danger p-button-text p-button-sm"
                        onClick={() => removeRow(rowIndex)}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        );
      }
      case 'image_block':
        return renderImageBlockEditor(data, idx);
      case 'carousel':
        return (
          <CarouselSectionEditor
            data={data}
            lang={watchLang}
            country={
              typeof watchCountry === 'string'
                ? watchCountry
                : watchCountry?.code || watchCountry?.slug || ''
            }
            onChange={(patch) => patchSectionData(idx, patch)}
          />
        );
      default:
        return (
          <Message severity="warn" text={`Unknown section type: ${section.type}`} className="w-full" />
        );
    }
  };

  const CompletionIndicator = ({ percentage }) => (
    <div className="flex align-items-center gap-3">
      <span
        className={`text-sm font-medium ${
          percentage === 100
            ? 'text-green-600'
            : percentage >= 70
              ? 'text-blue-600'
              : 'text-orange-600'
        }`}
      >
        {percentage === 100 ? `${percentage}% completed` : `${percentage}% progress`}
      </span>
      <ProgressBar
        value={percentage}
        style={{ width: '200px', height: '10px' }}
        showValue={false}
        color={percentage === 100 ? '#22c55e' : percentage >= 70 ? '#3b82f6' : '#f59e0b'}
      />
    </div>
  );

  if (isLoadingPage) {
    return (
      <div
        className="flex flex-column align-items-center justify-content-center"
        style={{ minHeight: '300px' }}
      >
        <i className="pi pi-spin pi-spinner" style={{ fontSize: '2rem', color: '#2020A0' }} />
        <div style={{ marginTop: '1rem' }}>Loading country page...</div>
      </div>
    );
  }

  return (
    <div className="p-4" style={{ paddingBottom: '6rem' }}>
      <Toast ref={toast} />
      <form onSubmit={handleSubmit(onSave)}>
        <Card
          title="Country page"
          subTitle="/{lang}/{country}"
          header={
            <div className="flex justify-content-between align-items-center px-3 pt-3">
              <h2 className="m-0">Country page editor</h2>
              {watchId ? (
                <CompletionIndicator
                  percentage={Math.round(
                    ([
                      watchLang,
                      watchCountry,
                      watchSlug,
                      watchFullSlug,
                      watchHero?.title,
                      watchHero?.description,
                      watchSections.length > 0,
                      metaTags?.some((t) => t.type?.code),
                      watchHero?.image?.url,
                      watchFaqsItems.length > 0,
                    ].filter(Boolean).length /
                      10) *
                      100
                  )}
                />
              ) : null}
            </div>
          }
        >
          <Panel header="Publish & URL" className="mb-3">
            <div className="grid">
              <div className="col-12 md:col-4">
                <label>
                  <strong>Language</strong>
                </label>
                <Controller
                  name="lang"
                  control={control}
                  rules={{ required: true }}
                  render={({ field }) => (
                    <Dropdown
                      {...field}
                      options={LANG_OPTIONS.filter((l) => l.code === 'EN' || l.code === 'ES')}
                      optionLabel="name"
                      placeholder="Select language"
                      className="w-full"
                    />
                  )}
                />
              </div>
              <div className="col-12 md:col-4">
                <label>
                  <strong>Country</strong>
                </label>
                <Controller
                  name="country"
                  control={control}
                  rules={{ required: true }}
                  render={({ field }) => (
                    <Dropdown
                      value={
                        typeof field.value === 'string'
                          ? field.value
                          : field.value?.code || null
                      }
                      options={COUNTRY_OPTIONS}
                      optionLabel="name"
                      optionValue="code"
                      placeholder="Mexico or Canada"
                      className="w-full"
                      onChange={(e) => field.onChange(e.value || '')}
                    />
                  )}
                />
              </div>
              <div className="col-12 md:col-4">
                <label>
                  <strong>Full slug</strong>
                </label>
                <Controller
                  name="full_slug"
                  control={control}
                  render={({ field }) => (
                    <InputText {...field} className="w-full" disabled />
                  )}
                />
              </div>
            </div>

            {!watchId && !watchRevalidated && (
              <div className="grid mt-2">
                <div className="col-8">
                  <div>
                    {public_url}
                    {watchFullSlug}
                  </div>
                </div>
                <div className="col-4">
                  <Button
                    loading={isLoading}
                    label="Create country page"
                    disabled={!isValidUrl}
                    className="main-button"
                    type="submit"
                  />
                </div>
              </div>
            )}

            {watchId && !watchRevalidated && (
              <div className="grid mt-2">
                <div className="col-8">
                  <div className="mb-3">
                    {isValidUrl && (
                      <Message
                        severity="warn"
                        text="This link is not yet available for public access yet."
                        className="border-primary w-full justify-content-start"
                      />
                    )}
                  </div>
                  <div>
                    {public_url}
                    {watchFullSlug}
                  </div>
                  {revalidationLoading && (
                    <div className="mt-2">
                      <ProgressBar
                        mode="indeterminate"
                        style={{ height: '7px', '--value-color': '#0D0D65' }}
                      />
                    </div>
                  )}
                </div>
                <div className="col-4">
                  <Button
                    loading={revalidationLoading}
                    onClick={handleFirstRevalidation}
                    label="Publish Now"
                    tooltip="Triggers the first revalidation of this country page's cache"
                    tooltipOptions={{ position: 'bottom' }}
                    severity="warning"
                    disabled={!isValidUrl}
                    className="p-button-primary w-full"
                    type="button"
                  />
                </div>
              </div>
            )}

            {watchId && watchRevalidated && (
              <>
                <div className="col-8">
                  <Message
                    severity="info"
                    text="This link is available for public access."
                    className="border-primary w-full justify-content-start"
                  />
                </div>
                <div className="col-12">
                  <a
                    href={`${public_url}${getValues('full_slug')}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="no-underline hover:opacity-80 transition-opacity"
                  >
                    <div className="col-12 flex align-items-center">
                      <label className="text-md font-semibold mb-0 mr-2 cursor-pointer">
                        <strong>{`${public_url}${getValues('full_slug')}`}</strong>
                      </label>
                      <i className="pi pi-external-link" style={{ fontSize: '0.9rem' }} />
                    </div>
                  </a>
                </div>
              </>
            )}
          </Panel>
        </Card>

        {showContentPanels && (
          <>
            <Card className="mt-4" title="Basic info">
              <Panel header="Hero copy on this country page" className="mb-3">
                <Message
                  severity="info"
                  className="w-full mb-3"
                  text="Eyebrow, title and description render above the search on this /{lang}/{country} page. /es and /en are edited in Home pages."
                />
                <div className="grid">
                  <div className="col-12">
                    <label>
                      <strong>Breadcrumb</strong>
                    </label>
                    <Controller
                      name="hero.breadcrumb"
                      control={control}
                      render={({ field }) => <InputText {...field} className="w-full" />}
                    />
                  </div>
                  <div className="col-12">
                    <label>
                      <strong>Eyebrow</strong>
                    </label>
                    <Controller
                      name="hero.eyebrow"
                      control={control}
                      render={({ field }) => (
                        <InputText {...field} className="w-full" placeholder="Eyebrow" />
                      )}
                    />
                  </div>
                  <div className="col-12">
                    <label>
                      <strong>Title</strong>
                    </label>
                    <Controller
                      name="hero.title"
                      control={control}
                      render={({ field }) => <InputText {...field} className="w-full" />}
                    />
                  </div>
                  <div className="col-12">
                    <label>
                      <strong>Description</strong>
                    </label>
                    <Controller
                      name="hero.description"
                      control={control}
                      render={({ field }) => (
                        <InputTextarea {...field} rows={3} className="w-full" />
                      )}
                    />
                  </div>
                  <div className="col-12">
                    <div className="p-3 border-round border-1 surface-border surface-card">
                      <strong>Hero image</strong>
                      <div className="grid mt-2">
                        <div className="col-12">
                          <label>
                            <strong>URL</strong>
                          </label>
                          <Controller
                            name="hero.image.url"
                            control={control}
                            render={({ field }) => <InputText {...field} className="w-full" />}
                          />
                        </div>
                        <div className="col-12">
                          <Button
                            type="button"
                            label="Upload"
                            icon="pi pi-cloud-upload"
                            onClick={() =>
                              openCloudinary(({ url, width, height }) => {
                                setValue('hero.image.url', url);
                                setValue('image.url', url);
                                if (width) {
                                  setValue('hero.image.width', width);
                                  setValue('image.width', width);
                                }
                                if (height) {
                                  setValue('hero.image.height', height);
                                  setValue('image.height', height);
                                }
                              })
                            }
                          />
                        </div>
                        <div className="col-6">
                          <label>
                            <strong>Alt</strong>
                          </label>
                          <Controller
                            name="hero.image.alt"
                            control={control}
                            render={({ field }) => <InputText {...field} className="w-full" />}
                          />
                        </div>
                        <div className="col-6">
                          <label>
                            <strong>Caption</strong>
                          </label>
                          <Controller
                            name="hero.image.caption"
                            control={control}
                            render={({ field }) => <InputText {...field} className="w-full" />}
                          />
                        </div>
                        {watchHero?.image?.url && (
                          <div className="col-12">
                            <img
                              src={watchHero.image.url}
                              alt={watchHero.image.alt || ''}
                              style={{
                                maxWidth: '100%',
                                maxHeight: 220,
                                borderRadius: 8,
                              }}
                            />
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </Panel>

              <Panel header="Featured crossing places" className="mb-3">
                <Message
                  severity="info"
                  className="w-full mb-3"
                  text="Select Crossing Name Places to associate with this country page (community / map)."
                />
                <Controller
                  name="featuredPlaceIds"
                  control={control}
                  render={({ field }) => (
                    <MultiSelect
                      value={field.value || []}
                      options={placeOptions}
                      onChange={(e) => field.onChange(e.value)}
                      filter
                      display="chip"
                      placeholder="Select places"
                      className="w-full"
                    />
                  )}
                />
              </Panel>
            </Card>

            <Card
              className="mt-4"
              title="Content & SEO"
              subTitle="Translations, editorial body, meta tags and FAQs"
            >
              <Panel
                header="Manage Translations"
                className="mb-3"
                toggleable
                collapsed={translationsPanelCollapsed}
                onToggle={(e) => setTranslationsPanelCollapsed(e.value)}
                expandIcon="pi pi-chevron-down"
                collapseIcon="pi pi-chevron-up"
              >
                <TranslationsManager
                  translations={watchTranslations || []}
                  onAddTranslation={handleAddTranslation}
                  onRemoveTranslation={handleRemoveTranslation}
                  control={control}
                  currentLang={watchLang?.code || watchLang}
                  currentPageId={String(getValues('_id') || '')}
                  idField="country_page_id"
                  endpoint="/country_pages/translations"
                />
              </Panel>

              <Panel
                header="Manage Content"
                className="mb-3"
                toggleable
                collapsed={contentPanelCollapsed}
                onToggle={(e) => setContentPanelCollapsed(e.value)}
                expandIcon="pi pi-chevron-down"
                collapseIcon="pi pi-chevron-up"
              >
                <div className="flex flex-wrap justify-content-end gap-2 align-items-end mb-3">
                  <div style={{ minWidth: '14rem', maxWidth: '22rem' }}>
                    <label>
                      <strong>Add section</strong>
                    </label>
                    <Dropdown
                      value={selectedSectionType}
                      options={COUNTRY_SECTION_CATALOG}
                      optionLabel="label"
                      optionValue="code"
                      placeholder="Select section type"
                      className="w-full"
                      onChange={(e) => setSelectedSectionType(e.value)}
                    />
                  </div>
                  <Button
                    type="button"
                    label="Add section"
                    icon="pi pi-plus"
                    className="w-auto"
                    onClick={addSection}
                  />
                </div>
                {watchSections.length === 0 && (
                  <Message
                    severity="info"
                    text="No sections yet. Add heading, paragraph, image, or a dynamic border-cities table."
                    className="mb-3 block"
                  />
                )}
                {watchSections.map((section, index) => (
                  <div
                    key={section.id || `section-${index}`}
                    id={section.id ? `country-section-${section.id}` : undefined}
                    className="p-3 mb-3 border-1 border-round surface-border"
                  >
                    <div className="flex justify-content-between align-items-center mb-3">
                      <strong>
                        {index + 1}. {section.type}
                      </strong>
                      <div className="flex gap-1">
                        <Button
                          type="button"
                          icon="pi pi-arrow-up"
                          className="p-button-text p-button-sm"
                          disabled={index === 0}
                          onClick={() => moveSection(index, -1)}
                        />
                        <Button
                          type="button"
                          icon="pi pi-arrow-down"
                          className="p-button-text p-button-sm"
                          disabled={index === watchSections.length - 1}
                          onClick={() => moveSection(index, 1)}
                        />
                        <Button
                          type="button"
                          icon="pi pi-trash"
                          className="p-button-danger p-button-sm"
                          onClick={() => removeSection(index)}
                        />
                      </div>
                    </div>
                    {renderSectionEditor(section, index)}
                  </div>
                ))}
              </Panel>

              <Panel
                header="SEO & structured data"
                className="mb-3"
                toggleable
                collapsed={seoPanelCollapsed}
                onToggle={(e) => setSeoPanelCollapsed(e.value)}
                expandIcon="pi pi-chevron-down"
                collapseIcon="pi pi-chevron-up"
              >
                <div className="flex flex-wrap justify-content-end align-items-center gap-2 mb-3">
                  <Button
                    type="button"
                    label="Add SEO element"
                    icon="pi pi-plus"
                    className="w-auto"
                    onClick={addMetaTag}
                  />
                  <Button
                    type="button"
                    label="Generate SEO batch"
                    className="p-button-outlined w-auto"
                    onClick={() => {
                      setSeoPackSelected([]);
                      setSeoPackDialogVisible(true);
                    }}
                  />
                </div>
                {metaTags?.map((tag, tagIndex) => (
                  <div
                    key={tagIndex}
                    id={`seo-element-${tagIndex}`}
                    className="p-3 mb-3 border-round border-1 surface-border"
                  >
                    <div className="flex justify-content-between align-items-center mb-3">
                      <h4 className="m-0">
                        SEO element {tagIndex + 1}
                        {tag.name ? (
                          <span className="ml-2 text-sm font-normal text-color-secondary">
                            ({tag.name})
                          </span>
                        ) : null}
                      </h4>
                      <Button
                        icon="pi pi-trash"
                        className="p-button-danger p-button-sm"
                        type="button"
                        onClick={() => removeMetaTag(tagIndex)}
                      />
                    </div>
                    <label>
                      <strong>Type</strong>
                    </label>
                    <Dropdown
                      value={tag.type}
                      options={tagOptions}
                      filter
                      filterBy="label"
                      filterInputAutoFocus
                      optionLabel="label"
                      onChange={(e) => updateTagType(tagIndex, e.value)}
                      placeholder="Select a type"
                      className="w-full mb-2"
                    />
                    <label>
                      <strong>Content</strong>
                    </label>
                    <InputTextarea
                      value={tag.content || ''}
                      onChange={(e) => updateMainAttribute(tagIndex, 'content', e.target.value)}
                      rows={1}
                      autoResize
                      className="w-full mb-2"
                    />
                    {tag.attributes?.map((attr, attrIndex) => (
                      <div key={attrIndex} className="flex gap-2 mb-2">
                        <InputText
                          value={attr.key}
                          placeholder="key"
                          onChange={(e) =>
                            updateAttribute(tagIndex, attrIndex, 'key', e.target.value)
                          }
                          className="w-full"
                        />
                        <InputText
                          value={attr.value}
                          placeholder="value"
                          onChange={(e) =>
                            updateAttribute(tagIndex, attrIndex, 'value', e.target.value)
                          }
                          className="w-full"
                        />
                        <Button
                          icon="pi pi-times"
                          type="button"
                          className="p-button-text p-button-danger"
                          onClick={() => removeAttribute(tagIndex, attrIndex)}
                        />
                      </div>
                    ))}
                    <Button
                      type="button"
                      label="Add Attribute"
                      icon="pi pi-plus"
                      className="p-button-text p-button-sm"
                      onClick={() => addAttribute(tagIndex)}
                    />
                  </div>
                ))}
                <div className="mt-3">
                  <MetaTagsLiveCodePreview metaTags={getFinalMetaTags} />
                </div>
              </Panel>

              <Panel
                header="Manage FAQs"
                toggleable
                collapsed={faqsPanelCollapsed}
                onToggle={(e) => setFaqsPanelCollapsed(e.value)}
                expandIcon="pi pi-chevron-down"
                collapseIcon="pi pi-chevron-up"
              >
                <div className="grid">
                  <div className="col-12">
                    <label>
                      <strong>FAQ title (h3)</strong>
                    </label>
                    <Controller
                      name="faqs_section.h3"
                      control={control}
                      render={({ field }) => <InputText {...field} className="w-full" />}
                    />
                  </div>
                  <div className="col-12">
                    <label>
                      <strong>FAQ description</strong>
                    </label>
                    <Controller
                      name="faqs_section.p"
                      control={control}
                      render={({ field }) => (
                        <InputTextarea {...field} rows={2} className="w-full" />
                      )}
                    />
                  </div>
                  <div className="flex justify-content-end mb-3 col-12">
                    <Button
                      type="button"
                      label="Add FAQ"
                      icon="pi pi-plus"
                      className="w-auto"
                      onClick={handleAddFaq}
                    />
                  </div>
                  {watchFaqsItems.map((faq, idx) => (
                    <div
                      key={idx}
                      className="p-3 mb-2 border-1 border-round surface-border col-12"
                    >
                      <div className="flex justify-content-between align-items-center mb-2">
                        <strong>Question {idx + 1}</strong>
                        <Button
                          icon="pi pi-trash"
                          type="button"
                          className="p-button-danger p-button-text"
                          onClick={() => handleRemoveFaq(idx)}
                        />
                      </div>
                      <InputText
                        id={`faq-question-${idx}`}
                        value={faq.question || ''}
                        placeholder="Question"
                        className="w-full mb-2"
                        onChange={(e) => handleFaqChange(idx, 'question', e.target.value)}
                      />
                      <InputTextarea
                        value={faq.answer || ''}
                        placeholder="Answer"
                        rows={3}
                        className="w-full mb-2"
                        autoResize
                        onChange={(e) => handleFaqChange(idx, 'answer', e.target.value)}
                      />
                      <ProseMirrorComponent
                        content={faq.answer_html || ''}
                        onChange={(htmlContent) =>
                          handleFaqChange(idx, 'answer_html_updated', htmlContent)
                        }
                      />
                    </div>
                  ))}
                </div>
              </Panel>
            </Card>

            <Dialog
              header="Generate SEO batch"
              visible={seoPackDialogVisible}
              onHide={() => setSeoPackDialogVisible(false)}
              style={{ width: '32rem', maxWidth: '95vw' }}
              footer={
                <div className="flex justify-content-end gap-2">
                  <Button
                    type="button"
                    label="Cancel"
                    className="p-button-text"
                    onClick={() => setSeoPackDialogVisible(false)}
                    disabled={seoPackGenerating}
                  />
                  <Button
                    type="button"
                    label="Generate"
                    icon="pi pi-check"
                    onClick={handleGenerateSeoPack}
                    loading={seoPackGenerating}
                  />
                </div>
              }
            >
              <p className="mt-0 mb-3 text-sm text-color-secondary">
                Creates meta/schema tags and merges them (skips packs already present).
              </p>
              <div className="flex flex-column gap-3">
                {COUNTRY_SEO_PACK_OPTIONS.map((pack) => (
                  <div key={pack.id} className="flex align-items-start gap-2">
                    <Checkbox
                      inputId={`country-seo-pack-${pack.id}`}
                      checked={seoPackSelected.includes(pack.id)}
                      onChange={(e) => {
                        setSeoPackSelected((prev) => {
                          if (e.checked) {
                            return prev.includes(pack.id) ? prev : [...prev, pack.id];
                          }
                          return prev.filter((id) => id !== pack.id);
                        });
                      }}
                    />
                    <label htmlFor={`country-seo-pack-${pack.id}`} className="cursor-pointer">
                      <div className="font-medium">{pack.label}</div>
                      <div className="text-sm text-color-secondary">{pack.description}</div>
                    </label>
                  </div>
                ))}
              </div>
            </Dialog>

            <Dialog
              header="Country page preview"
              visible={previewVisible}
              onHide={() => setPreviewVisible(false)}
              style={{ width: '95vw', maxWidth: 1280 }}
              maximizable
              blockScroll
              dismissableMask
              contentStyle={{
                padding: 0,
                background: '#e5e7eb',
                maxHeight: '80vh',
                overflow: 'auto',
              }}
            >
              <div style={{ padding: '24px 16px' }}>
                <PreviewCountry
                  sections={watchSections}
                  hero={watchHero}
                  faqs_section={getValues('faqs_section')}
                  full_slug={watchFullSlug}
                />
              </div>
            </Dialog>

            <div
              style={{
                position: 'fixed',
                bottom: 0,
                left: '50%',
                transform: 'translateX(-50%)',
                width: '720px',
                maxWidth: '95vw',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                gap: '0.75rem',
                padding: '1rem',
                backgroundColor: 'white',
                boxShadow: '0 -2px 10px rgba(0, 0, 0, 0.1)',
                zIndex: 1000,
              }}
            >
              <Button
                type="button"
                label="Publish"
                disabled={isRevalidateLoading}
                loading={isRevalidateLoading}
                onClick={onRevalidateUrl}
                icon="pi pi-globe"
                severity="warning"
                style={{ width: '200px' }}
              />
              <div style={{ display: 'flex', gap: '0.75rem' }}>
                <Button
                  type="button"
                  label="Preview"
                  icon="pi pi-eye"
                  className="p-button-outlined"
                  onClick={() => setPreviewVisible(true)}
                  style={{ width: '160px' }}
                />
                <Button
                  loading={isLoading}
                  label="Save"
                  icon="pi pi-save"
                  className="main-button"
                  type="submit"
                  style={{ width: '200px' }}
                />
              </div>
            </div>
          </>
        )}
      </form>
    </div>
  );
}

export default CountryPagesManager;
